import type { JSX } from 'react';

import { RevvlyStatusIcon } from '@admin-platform/ui/components';
import classNames from 'classnames';

import { usePlatformStatus } from '../../hooks/usePlatformStatus';
import { websiteUrl } from '../../utils/env';

import { APP_RELEASE_VERSION } from './appRelease';
import { iconLabel, shortVersion } from './revvlyIconState';
import styles from './SignInStatus.module.css';

/**
 * One line under the sign-in form: what is happening to Revvly before anyone logs in. A problem
 * first ("Background work is slow"); otherwise what is new, linking to the public changelog.
 * The release face never animates here: there is no person yet, so nothing is "unread".
 */
export function SignInStatus(): JSX.Element {
  const platform = usePlatformStatus(APP_RELEASE_VERSION ?? null);
  const trouble = platform.state !== 'ok';
  const face = trouble ? (platform.state === 'degraded' ? 'degraded' : 'down') : 'idle';
  const release = platform.release;

  let text: string;
  if (platform.state === 'unreachable') text = "Revvly can't be reached right now. Retrying.";
  else if (trouble) text = iconLabel(face, platform);
  else if (release?.headline) text = `New in ${shortVersion(release.version)}: ${release.headline}`;
  else text = 'All systems normal';

  const minor = release?.version.match(/^v\d+\.\d+/)?.[0];
  const changelog =
    !trouble && release && websiteUrl ? `${websiteUrl}/docs/changelog#${minor}` : null;

  return (
    <p
      className={classNames(styles.Line, {
        [styles.Degraded]: face === 'degraded',
        [styles.Down]: face === 'down',
      })}
      role="status"
    >
      <RevvlyStatusIcon state={face} className={styles.Glyph} />
      <span className={styles.Text}>
        {text}
        {changelog && (
          <>
            {' '}
            <a href={changelog} target="_blank" rel="noopener noreferrer">
              Release log
            </a>
          </>
        )}
      </span>
    </p>
  );
}
